import { EditSectionControls } from '@components/shared/edit-section-controls.component';
import { SectionCard } from '@components/shared/section-card.component';
import { Badge } from '@components/ui/badge';
import { Checkbox } from '@components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip';
import { cn } from '@lib/utils';
import { hasArrayChanges } from '@utils/array.util';
import { HelpCircleIcon } from 'lucide-react';
import React, { type FC, useCallback, useMemo, useState } from 'react';

type CheckboxOption<T extends string> = {
  description?: string;
  label: string;
  value: T;
};

type CheckboxEditSectionProps<T extends string> = {
  /**
   * Allow saving even when no changes are detected
   */
  allowSaveWithoutChanges?: boolean;
  /**
   * If provided, a custom color class for the badge (e.g., 'bg-blue-100 text-blue-700')
   * Only used when renderBadge is not provided.
   */
  badgeClassName?: string;
  /**
   * Disable editing when true
   */
  disabled?: boolean;
  /**
   * Message to show when the edit button is disabled
   */
  disabledTooltip?: string;
  /**
   * Message to show when no items are selected
   */
  emptyMessage?: string;
  /**
   * Whether the component is in a busy/loading state
   */
  isBusy?: boolean;
  /**
   * The currently selected values
   */
  items: T[];
  /**
   * Callback when items are saved
   */
  onSave: (items: T[]) => Promise<void> | void;
  /**
   * Available options to choose from
   */
  options: CheckboxOption<T>[];
  /**
   * Custom badge render function for displaying selected items
   */
  renderBadge?: (item: T) => React.ReactNode;
  /**
   * Section title
   */
  title: string;
  /**
   * Optional tooltip content for the title
   */
  titleTooltip?: React.ReactNode;
};

export const CheckboxEditSection = <T extends string>(props: CheckboxEditSectionProps<T>): ReturnType<FC> => {
  const {
    allowSaveWithoutChanges = false,
    badgeClassName = 'bg-blue-100 text-blue-700 hover:bg-blue-100',
    disabled = false,
    emptyMessage = 'No items selected',
    isBusy = false,
    items,
    onSave,
    options,
    renderBadge,
    title,
    titleTooltip,
  } = props;

  const [localItems, setLocalItems] = useState<T[]>(items);
  const [isEditing, setIsEditing] = useState(false);

  // Sync local state when items prop changes
  const handleStartEdit = useCallback(() => {
    setLocalItems(items);
    setIsEditing(true);
  }, [items]);

  const hasChanges = useMemo(() => {
    return hasArrayChanges({ next: localItems, previous: items });
  }, [items, localItems]);

  const handleToggle = useCallback((value: T) => {
    setLocalItems((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value);
      }
      return [...prev, value];
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (allowSaveWithoutChanges && !hasChanges) {
      setIsEditing(false);
      return;
    }

    await onSave(localItems);
    setIsEditing(false);
  }, [allowSaveWithoutChanges, hasChanges, localItems, onSave]);

  const handleCancel = useCallback(() => {
    setLocalItems(items);
    setIsEditing(false);
  }, [items]);

  return (
    <SectionCard>
      <EditSectionControls
        cancelAriaLabel={`Cancel ${title}`}
        editAriaLabel={`Edit ${title}`}
        isCancelDisabled={isBusy}
        isEditDisabled={disabled}
        isEditing={isEditing}
        isSaveDisabled={isBusy || (!allowSaveWithoutChanges && !hasChanges)}
        onCancel={handleCancel}
        onEdit={handleStartEdit}
        onSave={handleSave}
        saveAriaLabel={`Save ${title}`}
      />
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {titleTooltip && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircleIcon className="size-3.5 text-gray-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs font-normal text-xs">{titleTooltip}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {isEditing ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2">
              {options.map((option) => (
                <div
                  key={option.value}
                  className="flex items-start gap-3 p-2 rounded hover:bg-gray-50"
                >
                  <Checkbox
                    checked={localItems.includes(option.value)}
                    disabled={isBusy}
                    id={`${title.toLowerCase().replace(/\s+/g, '-')}-${option.value}`}
                    onCheckedChange={() => handleToggle(option.value)}
                  />
                  <label
                    className="flex flex-col gap-0.5 cursor-pointer flex-1"
                    htmlFor={`${title.toLowerCase().replace(/\s+/g, '-')}-${option.value}`}
                  >
                    <span className="text-sm font-medium text-gray-900">{option.label}</span>
                    {option.description && <span className="text-xs text-gray-500">{option.description}</span>}
                  </label>
                </div>
              ))}
            </div>

            <div className="h-2" />
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {items.length > 0 ? (
              items.map((item) => {
                if (renderBadge) {
                  return <React.Fragment key={item}>{renderBadge(item)}</React.Fragment>;
                }
                const option = options.find((o) => o.value === item);
                return (
                  <Badge
                    key={item}
                    className={cn('border-transparent', badgeClassName)}
                    size="sm"
                    variant="secondary"
                  >
                    {option?.label ?? item}
                  </Badge>
                );
              })
            ) : (
              <span className="text-sm text-gray-500">{emptyMessage}</span>
            )}
          </div>
        )}
      </div>
    </SectionCard>
  );
};
